import React, { createContext, useContext, useReducer } from "react";

export type PriceTier = {
  min: number;
  price: number;
};

export type CartItem = {
  id: string; // variant_id as string
  name: string;
  variant_id: number;
  product_id?: number;
  variant_name?: string;
  quantity: number;
  tiers: PriceTier[];
  price: number;
  image?: string;
};

type CartState = {
  items: CartItem[];
};

type CartContextType = {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  addItem: (item: Omit<CartItem, "quantity" | "price" | "id">, qty?: number) => void;
  removeItem: (variant_id: number) => void;
  updateQty: (variant_id: number, qty: number) => void;
  clearCart: () => void;
};

const CartContext = createContext<CartContextType | null>(null);

const calculateTierPrice = (qty: number, tiers: PriceTier[]) =>
  [...tiers].sort((a, b) => b.min - a.min).find((t) => qty >= t.min)?.price ?? tiers[0].price;

const reducer = (state: CartState, action: any): CartState => {
  switch (action.type) {
    case "ADD": {
      const variant_id = action.item.variant_id;
      const idx = state.items.findIndex((i) => i.variant_id === variant_id);

      if (idx >= 0) {
        // Same variant -> increase qty
        const updatedItem = { ...state.items[idx] };
        updatedItem.quantity += action.qty;
        updatedItem.price = calculateTierPrice(updatedItem.quantity, updatedItem.tiers);

        const newItems = [...state.items];
        newItems[idx] = updatedItem;
        return { items: newItems };
      } else {
        // New item
        const newItem: CartItem = {
          ...action.item,
          id: action.item.variant_id.toString(),
          quantity: action.qty,
          price: calculateTierPrice(action.qty, action.item.tiers),
        };
        return { items: [...state.items, newItem] };
      }
    }

    case "UPDATE": {
      const newItems = state.items.map((i) =>
        i.variant_id === action.variant_id
          ? { ...i, quantity: action.qty, price: calculateTierPrice(action.qty, i.tiers) }
          : i
      );
      return { items: newItems };
    }

    case "REMOVE":
      return { items: state.items.filter((i) => i.variant_id !== action.variant_id) };

    case "CLEAR":
      return { items: [] };

    default:
      return state;
  }
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { items: [] });

  const ctx: CartContextType = {
    items: state.items,
    totalItems: state.items.reduce((a, b) => a + b.quantity, 0),
    totalPrice: state.items.reduce((a, b) => a + b.quantity * b.price, 0),

    addItem: (item, qty = 1) =>
      dispatch({ type: "ADD", item, qty }),

    updateQty: (variant_id, qty) =>
      dispatch({ type: "UPDATE", variant_id, qty }),

    removeItem: (variant_id) =>
      dispatch({ type: "REMOVE", variant_id }),

    clearCart: () => dispatch({ type: "CLEAR" }),
  };

  return <CartContext.Provider value={ctx}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};